/**
 * Modelo de navegación principal. Vive aparte del render para que el sidebar
 * (desktop) y el drawer (móvil) pinten exactamente los mismos ítems, y para que
 * agregar una sección sea tocar una lista y no un JSX de 200 líneas.
 *
 * El filtrado por permisos ocurre acá: un grupo que se queda sin ítems visibles
 * no se renderiza, así no quedan encabezados huérfanos.
 */
import {
  Boxes,
  Coins,
  CalendarDays,
  Inbox,
  Calendar,
  ClipboardList,
  FileText,
  FlaskConical,
  LayoutDashboard,
  MapPin,
  Pill,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  ShieldHalf,
  TrendingUp,
  Users,
  Users2,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Si está presente, el ítem solo se muestra si el usuario tiene el permiso. */
  permiso?: string;
  /** Match exacto de ruta (para el índice del tenant, que si no matchea todo). */
  end?: boolean;
  /**
   * Módulo que tiene que estar contratado para que este enlace exista.
   *
   * Sin esto el menú ofrece farmacia a quien no la tiene y el clic acaba en un
   * 404 del backend — que es correcto pero parece una avería.
   */
  funcionalidad?: string;
}

export interface NavGroup {
  id: string;
  /** null = grupo sin encabezado (el bloque de arriba, Dashboard). */
  label: string | null;
  items: NavItem[];
}

export function buildNav(
  /** Prefijo de las rutas. Hoy siempre "". Ver `useBase()`. */
  base: string,
  permisos: string[],
  /** Módulos contratados por el policlínico. Ver MeResponse.funcionalidades. */
  funcionalidades: string[] = [],
  /**
   * El super administrador pasa por todo, igual que en el servidor.
   *
   * Sin esto el menú le escondía media configuración —Personal entre otras—
   * mientras el backend le dejaba entrar escribiendo la URL a mano: la
   * pantalla existía, funcionaba, y no había forma de llegar a ella. Su rol no
   * lleva la lista de permisos del policlínico porque no los necesita; los
   * salta.
   */
  esSuperAdmin = false,
): NavGroup[] {
  const groups: NavGroup[] = [
          {
            id: "general",
            label: null,
            items: [
              // Primero lo que hay que HACER; el dashboard es para mirar cómo
              // va el negocio, no para trabajar el turno.
              { to: `${base}/mi-espacio`, label: "Mi Espacio", icon: Inbox },
              { to: base || "/", label: "Dashboard", icon: LayoutDashboard, end: true },
              // Gerencia va arriba y junto al dashboard, no en un cajón de
              // "reportes": para quien es socio es lo primero que abre, y solo
              // lo ve quien tiene el permiso.
              {
                to: `${base}/gerencia`,
                label: "Gerencia",
                icon: TrendingUp,
                permiso: "reporte:read",
              },
            ],
          },
          {
            id: "atencion",
            label: "Atención",
            items: [
              {
                // Primero Citas: recepción entra a dar horas, no a leer fichas.
                // Pacientes queda para consultar el historial de alguien.
                to: `${base}/citas`,
                label: "Citas",
                icon: CalendarDays,
                permiso: "paciente:read",
              },
              {
                to: `${base}/pacientes`,
                label: "Pacientes",
                icon: Users,
                permiso: "paciente:read",
              },
              // Agenda pide profesional:read, no paciente:read: es admin, médico
              // y recepción. Caja tiene paciente:read pero no gestiona agenda.
              {
                to: `${base}/agenda`,
                label: "Agenda",
                icon: Calendar,
                permiso: "profesional:read",
              },
              {
                // Antes había "Profesionales" y "Personal" por separado: era el
                // modelo de datos (profesional vs usuario) filtrado a la
                // interfaz, y un médico salía en ambas. Ahora es una lista.
                to: `${base}/personal`,
                label: "Personal",
                icon: Users2,
                permiso: "profesional:read_all",
              },
            ],
          },
          {
            id: "farmacia",
            label: "Farmacia",
            items: [
              {
                // Lo primero del mostrador: qué hay que despachar. El
                // inventario es consulta; despachar es el trabajo del turno.
                to: `${base}/farmacia/despacho`,
                label: "Despacho",
                icon: Pill,
                permiso: "farmacia:dispensar",
                funcionalidad: "farmacia",
              },
              {
                to: `${base}/farmacia/inventario`,
                label: "Inventario",
                icon: Boxes,
                permiso: "farmacia:read",
                funcionalidad: "farmacia",
              },
              {
                to: `${base}/farmacia/kardex`,
                label: "Kardex",
                icon: ScrollText,
                permiso: "farmacia:read",
                funcionalidad: "farmacia",
              },
              {
                // Valorización antes que compras: es lo que se mira cada
                // semana; el ingreso de mercadería solo cuando llega el camión.
                to: `${base}/farmacia/valorizacion`,
                label: "Valorización",
                icon: Coins,
                permiso: "farmacia:read",
                funcionalidad: "farmacia",
              },
              {
                to: `${base}/farmacia/compras`,
                label: "Compras",
                icon: Receipt,
                permiso: "farmacia:compra",
                funcionalidad: "farmacia",
              },
            ],
          },
          {
            id: "laboratorio",
            label: "Laboratorio",
            items: [
              {
                // Pide laboratorio:procesar y no :read — el médico también lee
                // órdenes, pero las suyas las ve dentro de la atención. Un
                // módulo entero en su menú sería la puerta de más otra vez.
                to: `${base}/laboratorio`,
                label: "Laboratorio",
                icon: FlaskConical,
                permiso: "laboratorio:procesar",
                funcionalidad: "laboratorio",
              },
            ],
          },
          {
            id: "configuracion",
            label: "Configuración",
            items: [
              {
                to: `${base}/ajustes`,
                label: "Ajustes",
                icon: Settings,
                permiso: "tenant:read",
              },
              {
                to: `${base}/roles`,
                label: "Roles",
                icon: ShieldHalf,
                permiso: "rol:read",
              },
              {
                to: `${base}/sedes`,
                label: "Sedes",
                icon: MapPin,
                permiso: "sede:create",
              },
              {
                to: `${base}/especialidades`,
                label: "Especialidades",
                icon: ClipboardList,
                permiso: "especialidad:assign",
              },
            ],
          },
          {
            id: "finanzas",
            label: "Finanzas",
            items: [
              {
                to: `${base}/caja`,
                label: "Caja",
                icon: Wallet,
                permiso: "pago:create",
              },
              {
                // pago:read y no pago:create: contabilidad y los médicos ven
                // lo cobrado sin poder cobrar.
                to: `${base}/pagos`,
                label: "Pagos",
                icon: Receipt,
                permiso: "pago:read",
              },
              {
                to: `${base}/facturacion`,
                label: "Facturación",
                icon: FileText,
                permiso: "comprobante:read",
                funcionalidad: "facturacion_electronica",
              },
            ],
          },
          {
            id: "sistema",
            label: "Sistema",
            items: [
              {
                to: `${base}/auditoria`,
                label: "Auditoría",
                icon: ShieldCheck,
                permiso: "auditoria:read",
                funcionalidad: "auditoria",
              },
            ],
          },
        ];

  return groups
    .map((g) => ({
      ...g,
      // Dos filtros, no uno: el permiso dice si esta persona puede y la
      // funcionalidad si el módulo existe aquí. Se pasan los dos porque un
      // administrador con todos los permisos de una clínica sin farmacia no
      // debe ver farmacia.
      items: g.items.filter(
        (i) =>
          (!i.permiso || esSuperAdmin || permisos.includes(i.permiso)) &&
          (!i.funcionalidad || funcionalidades.includes(i.funcionalidad)),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
